import { Injectable, signal } from '@angular/core';
import type { Preset } from '@primeuix/themes/types';
import {
  definePreset,
  usePreset,
  updatePrimaryPalette,
  updateSurfacePalette,
} from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import Lara from '@primeuix/themes/lara';
import Material from '@primeuix/themes/material';
import Nora from '@primeuix/themes/nora';
import { GRANDLS_OVERRIDES } from './grandls-theme';

// ---------------------------------------------------------------------------
// Preset registry
// ---------------------------------------------------------------------------

const PRESETS: Record<string, Preset> = {
  Aura: Aura as Preset,
  Lara: Lara as Preset,
  Material: Material as Preset,
  Nora: Nora as Preset,
};

export const PRESET_NAMES = Object.keys(PRESETS);

// ---------------------------------------------------------------------------
// Primary color palette options
// ---------------------------------------------------------------------------

export interface PrimaryColorOption {
  name: string;
  /** CSS hex value used for the swatch preview (500 shade). */
  preview: string;
}

/**
 * All PrimeNG primitive color palettes that can serve as the primary color.
 * The `preview` hex corresponds to the 500 shade of each palette.
 */
export const PRIMARY_COLORS: PrimaryColorOption[] = [
  { name: 'emerald', preview: '#10b981' },
  { name: 'green', preview: '#22c55e' },
  { name: 'lime', preview: '#84cc16' },
  { name: 'red', preview: '#ef4444' },
  { name: 'orange', preview: '#f97316' },
  { name: 'amber', preview: '#f59e0b' },
  { name: 'yellow', preview: '#eab308' },
  { name: 'teal', preview: '#14b8a6' },
  { name: 'cyan', preview: '#06b6d4' },
  { name: 'sky', preview: '#0ea5e9' },
  { name: 'blue', preview: '#3b82f6' },
  { name: 'indigo', preview: '#6366f1' },
  { name: 'violet', preview: '#8b5cf6' },
  { name: 'purple', preview: '#a855f7' },
  { name: 'fuchsia', preview: '#d946ef' },
  { name: 'pink', preview: '#ec4899' },
  { name: 'rose', preview: '#f43f5e' },
];

// ---------------------------------------------------------------------------
// Surface palette options
// ---------------------------------------------------------------------------

export interface SurfaceColorOption {
  name: string;
  /** CSS hex value used for the swatch preview (500 shade). */
  preview: string;
}

export const SURFACE_COLORS: SurfaceColorOption[] = [
  { name: 'slate', preview: '#64748b' },
  { name: 'gray', preview: '#6b7280' },
  { name: 'zinc', preview: '#71717a' },
  { name: 'neutral', preview: '#737373' },
  { name: 'stone', preview: '#78716c' },
];

// ---------------------------------------------------------------------------
// localStorage key
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'grandls-theme';

interface ThemeState {
  preset: string;
  primary: string;
  surface: string;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

@Injectable({ providedIn: 'root' })
export class ThemeService {
  /** Name of the currently active base preset. */
  readonly currentPreset = signal('Lara');

  /** Name of the currently active primary color palette. */
  readonly currentPrimary = signal('indigo');

  /** Name of the currently active surface palette. */
  readonly currentSurface = signal('slate');

  // -----------------------------------------------------------------------
  // Preset switching
  // -----------------------------------------------------------------------

  setPreset(name: string): void {
    const base = PRESETS[name];
    if (!base) return;

    // Apply the base preset with our semantic overrides merged in
    const preset = definePreset(base, GRANDLS_OVERRIDES as Preset);
    usePreset(preset);

    this.currentPreset.set(name);

    // Re-apply current primary and surface on top of the new preset
    this.applyPrimary(this.currentPrimary());
    this.applySurface(this.currentSurface());

    this.persist();
  }

  // -----------------------------------------------------------------------
  // Primary color switching
  // -----------------------------------------------------------------------

  setPrimary(colorName: string): void {
    this.applyPrimary(colorName);
    this.currentPrimary.set(colorName);
    this.persist();
  }

  // -----------------------------------------------------------------------
  // Surface switching
  // -----------------------------------------------------------------------

  setSurface(colorName: string): void {
    this.applySurface(colorName);
    this.currentSurface.set(colorName);
    this.persist();
  }

  // -----------------------------------------------------------------------
  // Restore from localStorage (called on app startup)
  // -----------------------------------------------------------------------

  restoreFromStorage(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;

      const state: ThemeState = JSON.parse(raw);

      // Only apply if all values are valid
      if (state.preset && PRESETS[state.preset]) {
        // Apply preset first (this also triggers primary/surface re-apply via setPreset)
        // but we want to use the stored primary/surface, so set signals first
        this.currentPrimary.set(state.primary ?? 'indigo');
        this.currentSurface.set(state.surface ?? 'slate');
        this.setPreset(state.preset);
      } else {
        // Just apply primary/surface if preset is default
        if (state.primary) {
          this.setPrimary(state.primary);
        }
        if (state.surface) {
          this.setSurface(state.surface);
        }
      }
    } catch {
      // Ignore corrupted localStorage
    }
  }

  // -----------------------------------------------------------------------
  // Private helpers
  // -----------------------------------------------------------------------

  private applyPrimary(colorName: string): void {
    updatePrimaryPalette({
      50: `{${colorName}.50}`,
      100: `{${colorName}.100}`,
      200: `{${colorName}.200}`,
      300: `{${colorName}.300}`,
      400: `{${colorName}.400}`,
      500: `{${colorName}.500}`,
      600: `{${colorName}.600}`,
      700: `{${colorName}.700}`,
      800: `{${colorName}.800}`,
      900: `{${colorName}.900}`,
      950: `{${colorName}.950}`,
    });
  }

  private applySurface(colorName: string): void {
    updateSurfacePalette({
      50: `{${colorName}.50}`,
      100: `{${colorName}.100}`,
      200: `{${colorName}.200}`,
      300: `{${colorName}.300}`,
      400: `{${colorName}.400}`,
      500: `{${colorName}.500}`,
      600: `{${colorName}.600}`,
      700: `{${colorName}.700}`,
      800: `{${colorName}.800}`,
      900: `{${colorName}.900}`,
      950: `{${colorName}.950}`,
    });
  }

  private persist(): void {
    const state: ThemeState = {
      preset: this.currentPreset(),
      primary: this.currentPrimary(),
      surface: this.currentSurface(),
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Quota exceeded or private browsing – ignore
    }
  }
}
